import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth.js';
import { ApiError } from '../api.js';

type Mode = 'user' | 'admin';

export function LoginPage() {
  const { loginUser, loginAdmin, state } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<Mode>('user');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (state.status === 'authed') {
    const dest = (location.state as { from?: string } | null)?.from ?? '/projects';
    navigate(dest, { replace: true });
  }

  async function handle(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'user') {
        await loginUser(email.trim(), password);
      } else {
        await loginAdmin(password);
      }
      const dest = (location.state as { from?: string } | null)?.from ?? '/projects';
      navigate(dest, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'INVALID_CREDENTIALS') setError('Email or password is incorrect.');
        else if (err.code === 'INVALID_PASSWORD') setError('That admin password didn’t match.');
        else if (err.code === 'ADMIN_PASSWORD_UNSET')
          setError('Admin password isn’t configured on the server.');
        else setError(err.message);
      } else {
        setError('Network error.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-sm mx-auto py-16">
      <h1 className="font-serif text-3xl mb-2">Sign in</h1>
      <p className="text-muted text-sm mb-8">
        {mode === 'user'
          ? 'Sign in to your Scriptvoice account.'
          : 'Sign in with the admin password (ops only).'}
      </p>
      <form onSubmit={handle} className="space-y-4">
        {mode === 'user' && (
          <div>
            <label className="block text-sm text-muted mb-1.5" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              autoComplete="email"
              className="w-full px-3 py-2 bg-white border border-soft rounded-md focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/60 text-sm"
              required
            />
          </div>
        )}
        <div>
          <label className="block text-sm text-muted mb-1.5" htmlFor="pw">
            {mode === 'user' ? 'Password' : 'Admin password'}
          </label>
          <input
            id="pw"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus={mode === 'admin'}
            autoComplete="current-password"
            className="w-full px-3 py-2 bg-white border border-soft rounded-md focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/60 font-mono text-sm"
            required
            minLength={1}
          />
        </div>
        {error && (
          <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={submitting || !password || (mode === 'user' && !email)}
          className="w-full bg-ink text-parchment py-2 rounded-md hover:bg-ink/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm tracking-wide"
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
        {mode === 'user' ? (
          <>
            <p className="text-center text-sm text-muted">
              Don’t have an account?{' '}
              <Link to="/signup" className="text-accent underline-offset-2 hover:underline">Sign up</Link>
            </p>
            <p className="text-center text-xs text-muted/70">
              <Link to="/forgot-password" className="underline-offset-2 hover:underline">Forgot password?</Link>
            </p>
            <p className="text-center text-xs text-muted/70">
              <button type="button" onClick={() => { setMode('admin'); setEmail(''); setPassword(''); setError(null); }} className="underline-offset-2 hover:underline">
                Admin sign-in
              </button>
            </p>
          </>
        ) : (
          <p className="text-center text-xs text-muted/70">
            <button type="button" onClick={() => { setMode('user'); setPassword(''); setError(null); }} className="underline-offset-2 hover:underline">
              ← Back to user sign-in
            </button>
          </p>
        )}
      </form>
    </div>
  );
}
